import React, { useState, useCallback } from "react";
import "./partner-form.m.css";
import CloseIcon from "../../../../../assets/icons/close";
import { ThankYouModal } from "../../../molecules/thankyou-modal";
import { useModalEffect } from "../../../../hooks/useModalEffect";

const INITIAL_FORM_STATE = {
  name: "",
  email: "",
  phone: "",
  company: "",
  designation: "",
  city: "",
  message: "",
};

export const PartnerForm = ({ onClose, formType = "Partner with us" }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});

  useModalEffect(true);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!validateEmail(value)) {
          error = "Enter a valid email";
        }
        break;
      case "phone":
        if (!validatePhone(value)) {
          error = "Enter a valid phone number";
        }
        break;
      case "company":
      case "designation":
      case "city":
      case "name":
        if (!value.trim()) {
          error = "Field cannot be empty";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (loading) return;

      const newErrors = {};
      Object.keys(formData).forEach((key) => {
        if (key !== "message") {
          const error = validateField(key, formData[key]);
          if (error) {
            newErrors[key] = error;
          }
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setLoading(true);

      try {
        const response = await fetch("/partner-enquiry-smtp", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, formType }),
        });

        if (!response.ok) {
          throw new Error("Submission failed");
        }

        setSubmitted(true);
      } catch {
        alert("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [formData, formType, loading]
  );

  return (
    <div styleName="form-overlay" onClick={onClose}>
      <div
        styleName={`form-modal`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* CLOSE ICON */}
        <div styleName="form-close" onClick={onClose}>
          <CloseIcon size={12} />
        </div>

        {/* FORM */}
        {!submitted && (
          <>
            {/* FORM */}
            <p styleName="form-title">
              Provide your information and our team will follow up on your
              research enquiry
            </p>

            <form onSubmit={handleSubmit}>
              <div styleName="form-row">
                <input
                  name="name"
                  placeholder="Name *"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && <p styleName="error-message">{errors.name}</p>}
              </div>

              <div styleName="form-row two">
                <div styleName="form-field">
                  <input
                    type="text"
                    name="email"
                    placeholder="Email *"
                    value={formData.email}
                    onChange={handleChange}
                    styleName="half"
                  />
                  {errors.email && <p styleName="error-message">{errors.email}</p>}
                </div>
                <div styleName="form-field">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone *"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && <p styleName="error-message">{errors.phone}</p>}
                </div>
              </div>

              <div styleName="form-row">
                <input
                  name="company"
                  placeholder="Company *"
                  value={formData.company}
                  onChange={handleChange}
                />
                {errors.company && <p styleName="error-message">{errors.company}</p>}
              </div>

              <div styleName="form-row two">
                <div styleName="form-field">
                  <input
                    name="designation"
                    placeholder="Designation *"
                    value={formData.designation}
                    onChange={handleChange}
                    styleName="half"
                  />
                  {errors.designation && <p styleName="error-message">{errors.designation}</p>}
                </div>
                <div styleName="form-field">
                  <input
                    name="city"
                    placeholder="City *"
                    value={formData.city}
                    onChange={handleChange}
                  />
                  {errors.city && <p styleName="error-message">{errors.city}</p>}
                </div>
              </div>

              <div styleName="form-row">
                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                />
              </div>

              <div styleName="form-submit">
                <button
                  type="submit"
                  styleName="form-submit-button"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit enquiry request"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <ThankYouModal
        isVisible={submitted}
        onClose={() => {
          setFormData(INITIAL_FORM_STATE);
          setErrors({});
          onClose();
        }}
      />
    </div>
  );
};
